import {useEffect, useState} from 'react';

import {useNavigate, useParams} from 'react-router-dom';

import {Grid, Stack} from '@mantine/core';

import {RecordForm} from '../forms/RecordForm';

import {
    FormPageLayout,
    PlatformLoader,
    SimplePageContentWrapper,
    TabbedComponent,
} from '@flexinet/ui-components';
import {Profile} from '../profile/Profile';

import {EditPageNavigation} from '../navigation/EditPageNavigation';
import {useApplication} from '../hooks/useApplication.ts';
import {useApplicationsSelection} from '../hooks/useApplicationsSelection.ts';
import {useEditPageTools} from '../hooks/useEditPageTools';
import {useApplicationInfrastructure} from '../hooks/useApplicationInfrastructure';
import {rbacActions} from "../../../config/rbac/actions.ts";
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {IndexPage as ApplicationsAccountsPage} from "../../application-accounts/pages/IndexPage.tsx";
import {IndexPage as ApplicationProductsPage} from "../../application-products/pages/IndexPage.tsx";
import {ApplicationProgressSection} from '../components/ApplicationProgressSection.tsx';
import {ApplicationInfrastructureSection} from '../components/ApplicationInfrastructureSection.tsx';
import {ProvisioningMetadataForm} from '../forms/ProvisioningMetadataForm';
import {ProvisionFormDrawer} from '../drawers/ProvisionFormDrawer';
import {stage} from '../../../config/stage';


export const EditPage = () => {

    const navigate = useNavigate();

    const {allowedActions} = useVerifiedPermissions()

    const {id: applicationId} = useParams();

    if (typeof applicationId === 'undefined') {
        throw new Error('applicationId is required parameter');
    }

    const {
        record: application,
        updateRecord,
        provisionRecord,
        provisioning,
        deprovisionRecord,
        markRecordDecommissioned,
    } = useApplication(applicationId);

    const {handleClearSelection} = useApplicationsSelection();

    // FNP-1601: the infrastructure INVENTORY, which is what the server's
    // teardown actually reads. The page already loads it for the Infrastructure
    // inventory tab below; reading it here too costs no extra call and is what
    // lets the Decommission button show the blast radius the server will act on
    // rather than one derived from the demoted `Application` fields.
    const {records: infrastructureRecords} = useApplicationInfrastructure(applicationId);

    // ⚠️ THE CURRENT PROVISIONING ACT, mirroring console-api's
    // `getCurrentInfrastructure`: the records arrive newest-first and at most
    // one is non-terminal at a time, so the first non-DECOMMISSIONED row is the
    // live one. A DECOMMISSIONED row describes infrastructure that is gone, and
    // reading it as current would offer to tear down what no longer exists.
    const currentInfrastructure = infrastructureRecords
        .filter((r) => r.infrastructureStatus !== 'DECOMMISSIONED')[0];

    // FNP-1013: the provision dialog. Local state rather than a route, matching
    // how the decommission confirmation works — it is a choice made about the
    // record in front of you, not a place you navigate to.
    const [provisionOpen, setProvisionOpen] = useState(false);

    // FNP-998: Decommission renders in the page-header toolbar with every other
    // action, per the platform convention. The hook needs the record (for its
    // eligibility check), the stage being torn down, and the handler.
    const {
        mode,
        tools,
    } = useEditPageTools(
        application,
        stage === 'ide' ? 'dev' : stage,
        () => {
            // The SAME stage the button used for its eligibility check is the
            // one torn down — read once so the check and the action can never
            // disagree about which environment is being destroyed.
            void deprovisionRecord(stage === 'ide' ? 'dev' : stage);
        },
        // FNP-1007: correct the record instead of tearing anything down.
        //
        // ⚠️ Takes NO stage, unlike the handler above. The server's sweep is
        // application-wide — stacks, buckets, repositories and pipelines across
        // every environment — so passing one would imply a narrowing that does
        // not happen.
        //
        // The result is RETURNED rather than swallowed: a refusal resolves
        // normally with `marked: false`, and the modal renders `found` and
        // `incompleteChecks` distinctly from it.
        () => markRecordDecommissioned(),
        // FNP-1013: open the provision dialog. The playbook and stage are
        // chosen there, not here — the whole point is that the caller supplies
        // the playbook id rather than the code deriving one.
        () => setProvisionOpen(true),
        // FNP-1601: so the Decommission button's stack list and its eligibility
        // refusal both come from the record the server reads.
        currentInfrastructure,
    );


    useEffect(() => {
        handleClearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only effect: deliberately runs once, adding deps would re-fire it
    }, []);


    const tabs = []
    if (allowedActions.includes(rbacActions.ListApplicationAccounts)) {
        tabs.push({
                      title: 'Accounts',
                      component: <ApplicationsAccountsPage/>
                  })
    }
    if (allowedActions.includes(rbacActions.ListApplicationProducts)) {
        tabs.push({
                      title: 'Products',
                      component: <ApplicationProductsPage/>
                  })
    }

    // FNP-998: repairing provisioning-resource pointers. Gated on
    // UpdateApplication because it writes to the record — the same permission
    // the main edit form requires.
    //
    // This is what unblocks decommissioning an application provisioned before
    // `infraStackNames` was persisted at create: the teardown refuses without a
    // stack name and will not guess one, so an admin fills it in here.
    //
    // ═══ FNP-1588: THE TAB IS NAMED "RESOURCE POINTERS", NOT "PROVISIONING" ═══
    //
    // It was called "Provisioning" and sat beside a toolbar button called
    // "Provision", so the page read as if the two were one feature reached two
    // ways. They are unrelated: the button STARTS a provisioning run, this tab
    // STARTS NOTHING — it corrects fields on a record whose infrastructure
    // already exists.
    //
    // The owner's report was "the provisioning tab and the provision button
    // ... duplicate". They do not duplicate; nothing here was ever a second
    // entry point, and deleting either would have been a real regression. The
    // duplication was in the WORDS, so the words are what changed.
    //
    // "Resource pointers" says what the form edits: the stored names and ids
    // that point at resources living elsewhere. Correcting one moves nothing.
    if (allowedActions.includes(rbacActions.UpdateApplication) && application) {
        tabs.push({
                      title: 'Resource pointers',
                      component: <ProvisioningMetadataForm
                          record={application}
                          handleSubmit={(values) => updateRecord({
                              ...values,
                              id: application.id,
                              // ⚠️ `name` is deliberately NOT sent. FNP-176
                              // rejects the field's PRESENCE, not a change to
                              // it — "semantic immutability, not equality" — so
                              // sending it unchanged is still refused. It was
                              // sent here originally because the schema
                              // declared it required; FNP-998 made it optional,
                              // which was the actual bug.
                          } as never)}
                          handleCancel={() => navigate(-1)}
                      />
                  })
    }

    // FNP-1016: the infrastructure INVENTORY — what resources exist, as a
    // record separate from the application itself. This tab reads and writes
    // the `ApplicationInfrastructure` entity, and is where an admin registers
    // the seven legacy production applications this platform never provisioned.
    //
    // ⚠️ FNP-1588: this comment used to open by distinguishing itself from
    // "the Provisioning tab above" — a paragraph of prose doing work the two
    // labels should have been doing. When a file needs to explain that two
    // adjacent tabs are different things, the labels are the defect, and the
    // reader of the UI never sees the explanation anyway.
    //
    // "Infrastructure inventory" against "Resource pointers" carries the
    // distinction on screen: an INVENTORY of resources that exist, against the
    // stored POINTERS to them on the application record. The note above is now
    // describing the code, not propping up the labels.
    //
    // Gated on UpdateApplication: registering writes an inventory record for
    // the application, so it needs the same permission as editing it.
    if (allowedActions.includes(rbacActions.UpdateApplication) && application) {
        tabs.push({
                      title: 'Infrastructure inventory',
                      component: <ApplicationInfrastructureSection application={application}/>
                  })
    }


    if (!application) {
        return <PlatformLoader message='Loading application...Please wait'/>;
    }


    return (
        <FormPageLayout
            title={application.name ? application.name : application.id}
            pageNavigation={<EditPageNavigation component={application}/>}
            tools={tools}
        >

            {/*
              * FNP-1500: a drawer on the PlatformDrawer chain, showing the four
              * steps and every derived name before anything is built.
              *
              * ⚠️ The drawer closes ITSELF, after `onConfirm` resolves. It used
              * to be closed here in the same tick as the call, so a refusal
              * from the server destroyed the operator's input and toasted into
              * a dialog that was no longer on screen.
              */}
            <ProvisionFormDrawer
                opened={provisionOpen}
                onClose={() => setProvisionOpen(false)}
                provisioning={provisioning}
                record={application}
                onConfirm={async ({
                                     playbookId,
                                     stage: provisionStage,
                                     subdomain,
                                     deploymentAccountId,
                                     baseName,
                                     addressing,
                                 }) => {
                    // FNP-1542: `baseName` MUST be forwarded.
                    //
                    // ⚠️ IT USED TO BE DESTRUCTURED AWAY RIGHT HERE. The drawer
                    // collected it, showed every name derived from it, checked
                    // each against what exists — and then this handler dropped
                    // it on the floor. The orchestrator fell back to the
                    // application's `name`, so the panel validated names that
                    // were never created.
                    //
                    // That was survivable while the name was itself a slug.
                    // FNP-1537 freed the name to be human ("Flexi Testing"),
                    // and the fallback then produced a repository name
                    // CodeCommit refuses outright — so every provision failed.
                    // FNP-1568: `addressing` is forwarded for the same reason,
                    // and is the third field this statement has had to learn.
                    // The guard in fnp-1561-no-field-goes-nowhere.test.ts
                    // fails when a field the drawer submits is not named here.
                    await provisionRecord(
                        playbookId, provisionStage, subdomain, deploymentAccountId,
                        baseName, addressing,
                    );
                }}
            />

            {mode === 'view' ?

                <Stack gap={'xl'}>
                    <ApplicationProgressSection application={application}/>

                    <Grid>
                        <Grid.Col span={{
                            base: 12,
                            lg: 4
                        }}>
                            <Profile
                                record={application}
                            />
                        </Grid.Col>
                        <Grid.Col span={{
                            base: 12,
                            lg: 8
                        }}>
                            <TabbedComponent tabs={tabs}/>
                        </Grid.Col>
                    </Grid>

                </Stack>

                :
                <SimplePageContentWrapper>
                    <RecordForm
                        record={application}
                        handleSubmit={updateRecord}
                        handleCancel={() => navigate('/applications')}
                    />
                </SimplePageContentWrapper>
            }


        </FormPageLayout>
    );
};
